import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { PageLayout } from '@/shared/components/PageLayout';
import { Drawer } from '@/shared/components/Drawer';
import { useGetPluginInstances } from '@/shared/services/instances/hooks/use-get-plugin-instances';
import { useStyles } from '@/pages/PluginInstances/styles';
import { routes } from '@/pages/routes';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { InstanceCreate } from './components/InstanceCreate';
import { InstanceDetails } from './components/InstanceDetails';
const columns = [
    { value: 'guid', label: 'Идентификатор', minWidth: 280 },
    { value: 'name', label: 'Название агента', minWidth: 200 },
];
const rowsPerPageSetting = [10, 30, 50, 100, 1000];
const DEFAULT_PAGE_SIZE = 50;
const PAGE_NUMBER_PARAM = 'pageNumber';
const PAGE_SIZE_PARAM = 'pageSize';
// Пагинация хранится в query-параметрах, чтобы после F5 открывалась та же страница.
// Невалидные значения из адресной строки заменяются значениями по умолчанию.
const parsePagination = (search) => {
    const params = new URLSearchParams(search);
    const pageNumber = Number(params.get(PAGE_NUMBER_PARAM));
    const pageSize = Number(params.get(PAGE_SIZE_PARAM));
    return {
        pageNumber: Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1,
        pageSize: rowsPerPageSetting.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    };
};
export const PluginInstances = () => {
    const classes = useStyles();
    const history = useHistory();
    const { search } = useLocation();
    const { pageNumber, pageSize } = parsePagination(search);
    const { guid: openedInstanceGuid } = useParams();
    const isAddingInstance = Boolean(useRouteMatch({ path: routes.pluginInstanceAdd, exact: true }));
    const { data: pluginInstances } = useGetPluginInstances(`?pageSize=${pageSize}&pageNumber=${pageNumber}`);
    // Переходы между списком и drawer сохраняют query-параметры - иначе пагинация сбросится.
    const navigate = useCallback((pathname) => {
        history.push({ pathname, search: history.location.search });
    }, [history]);
    const setPagination = (nextPageNumber, nextPageSize, replace = false) => {
        const params = new URLSearchParams(history.location.search);
        params.set(PAGE_NUMBER_PARAM, String(nextPageNumber));
        params.set(PAGE_SIZE_PARAM, String(nextPageSize));
        const location = { search: `?${params.toString()}` };
        if (replace) {
            history.replace(location);
        }
        else {
            history.push(location);
        }
    };
    const totalCount = pluginInstances?.totalCount ?? 0;
    const totalPages = pluginInstances?.totalPages ?? 0;
    // После удаления строк или ручной правки URL страница может оказаться за пределами списка.
    useEffect(() => {
        if (totalPages > 0 && pageNumber > totalPages) {
            setPagination(totalPages, pageSize, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalPages, pageNumber, pageSize]);
    // Черновик номера страницы - применяется по Enter или потере фокуса, а не на каждый символ.
    const [pageDraft, setPageDraft] = useState(String(pageNumber));
    useEffect(() => {
        setPageDraft(String(pageNumber));
    }, [pageNumber]);
    const applyPageDraft = () => {
        const parsed = Number(pageDraft);
        if (!Number.isInteger(parsed)) {
            setPageDraft(String(pageNumber));
            return;
        }
        const nextPage = Math.min(Math.max(parsed, 1), Math.max(totalPages, 1));
        setPageDraft(String(nextPage));
        if (nextPage !== pageNumber) {
            setPagination(nextPage, pageSize);
        }
    };
    const handlePageKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            applyPageDraft();
        }
    };
    const handleCloseInstance = () => {
        navigate(routes.pluginInstances);
    };
    // useCallback - InstanceCreate дёргает onCreated из useEffect по ответу бэкенда.
    const handleInstanceCreated = useCallback((createdGuid) => {
        navigate(`/plugin-instance/${createdGuid}`);
    }, [navigate]);
    const handleChangePage = (_, newPage) => {
        setPagination(newPage + 1, pageSize);
    };
    const handleChangeRowsPerPage = (event) => {
        setPagination(1, Number(event.target.value));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(PageLayout, null,
            React.createElement(Box, { height: "75vh" },
                React.createElement(Box, { mb: 1 },
                    React.createElement(Typography, { variant: "h4" }, "\u0410\u0433\u0435\u043D\u0442\u044B")),
                React.createElement(Box, { mb: 2 },
                    React.createElement(Button, { variant: "contained", color: "primary", startIcon: React.createElement(AddCircleIcon, null), onClick: () => navigate(routes.pluginInstanceAdd) }, "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C")),
                React.createElement(Paper, { className: classes.tableRoot },
                    React.createElement(TableContainer, { className: classes.tableContainer },
                        React.createElement(Table, { stickyHeader: true },
                            React.createElement(TableHead, null,
                                React.createElement(TableRow, null, columns.map((column) => (React.createElement(TableCell, { key: column.value, style: { minWidth: column.minWidth } }, column.label))))),
                            React.createElement(TableBody, null, pluginInstances?.pluginInstanceInfoWithHeadAddresses?.map((instance) => (React.createElement(TableRow, { className: classes.tableBodyRow, key: instance.guid, hover: true, onClick: () => navigate(`/plugin-instance/${instance.guid}`) }, columns.map((column) => (React.createElement(TableCell, { key: column.value }, instance[column.value]))))))))),
                    totalCount > 0 && (React.createElement(Box, { display: "flex", alignItems: "center", justifyContent: "flex-end", pl: 2 },
                        React.createElement(TextField, { className: classes.pageInput, size: "small", variant: "outlined", type: "number", label: `Страница из ${totalPages}`, inputProps: { min: 1, max: totalPages }, value: pageDraft, onChange: (event) => setPageDraft(event.target.value), onBlur: applyPageDraft, onKeyDown: handlePageKeyDown }),
                        React.createElement(TablePagination, { labelRowsPerPage: 'Cтрок на странице', rowsPerPageOptions: rowsPerPageSetting, component: "div", count: totalCount, rowsPerPage: pageSize, page: pageNumber - 1, onChangePage: handleChangePage, onChangeRowsPerPage: handleChangeRowsPerPage })))))),
        React.createElement(Drawer, { isOpen: isAddingInstance, onClose: handleCloseInstance }, isAddingInstance && React.createElement(InstanceCreate, { onCreated: handleInstanceCreated })),
        React.createElement(Drawer, { isOpen: Boolean(openedInstanceGuid), onClose: handleCloseInstance }, openedInstanceGuid && React.createElement(InstanceDetails, { key: openedInstanceGuid, guid: openedInstanceGuid }))));
};
